import React from 'react';
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Button } from '../ui/button';
import { BookOpen, HelpCircle } from 'lucide-react';
import { HelpDocExport } from '../HelpDocExport';

// ────────────────────────────────────────────────────────────────
// Maintenance "How to Use" helpers — mirror the styling of
// EventRulesHelpPanel / AlertsHelpPanel / BeaconsHelpPanel exactly.
// One slide-out Sheet per modal: Service Schedule, Log Service,
// Edit Service Log and the Import Wizard.
// ────────────────────────────────────────────────────────────────

const Section = ({ title, children }) => (
  <div className="space-y-2">
    <h3 className="text-sm font-semibold text-[#2563eb]">{title}</h3>
    <div className="text-xs text-slate-700 dark:text-slate-300 space-y-2 leading-relaxed">{children}</div>
  </div>
);

const MiniTimeline = ({ steps }) => (
  <div className="flex items-stretch gap-0 pt-1">
    {steps.map((st, i) => (
      <React.Fragment key={i}>
        <div className="flex flex-col items-center flex-1 min-w-0">
          <span className="text-[9px] font-bold text-[#2563eb]">{st.t}</span>
          <span className="w-2 h-2 rounded-full bg-[#2563eb] my-0.5" />
          <span className="text-[9px] text-slate-600 dark:text-slate-300 text-center leading-tight">{st.label}</span>
        </div>
        {i < steps.length - 1 && <div className="h-px bg-[#2563eb]/40 flex-1 mt-[18px] min-w-[8px]" />}
      </React.Fragment>
    ))}
  </div>
);

// ─────────────── Per-field help tips (mirror EventRuleHelp's HelpTip) ───────────────

const MAINT_HELP_TOPICS = {
  device: {
    title: 'Device / Asset',
    body: ['Choose the vehicle, trailer or equipment this maintenance task belongs to. Every schedule tracks exactly one asset — its odometer, engine hours or dates drive the due point.'],
    example: 'Same 10,000 km oil change on 5 trucks → create 5 schedules (duplicate one, or use the Import wizard for bulk).',
    pitfall: 'Locked while editing — a schedule cannot move to another vehicle. Duplicate it or create a new one for the other asset.',
  },
  preset: {
    title: 'Task Preset',
    body: ['Quick-fills the Task Name with a common service type so naming stays consistent across the fleet — consistent names group cleanly in the Cost Report.'],
    example: 'Pick "Engine Oil Change" → the Task Name fills in; you can still edit the text afterwards.',
    pitfall: 'The preset only fills the name — it never changes intervals or basis. Pick Custom to type your own.',
  },
  basis: {
    title: 'Schedule Basis',
    body: [
      'What the countdown runs on:',
      '• Odometer (km) — due at a distance, from the vehicle\u2019s live odometer.',
      '• Engine Hours — due at a run-time; best for machinery and idle-heavy assets.',
      '• Calendar (days) — due on a date.',
      '• Dual (Odometer + Calendar or Engine Hours + Calendar) — both run side by side; whichever limit is reached first makes the service due.',
    ],
    timeline: [{ t: 'T0', label: 'Last serviced (reading / date)' }, { t: '…', label: 'Usage accrues' }, { t: 'Due', label: 'Interval reached' }],
    example: 'Oil change every 10,000 km OR 12 months, whichever first → dual basis Odometer + Calendar.',
    pitfall: 'Locked while editing. Odometer / engine-hours bases need that source enabled on the vehicle (Fleet › Edit Asset).',
  },
  interval: {
    title: 'Service Interval',
    body: ['How much usage between services, counted from the Last Serviced point. Next due = Last Serviced + interval.'],
    timeline: [{ t: 'T0', label: 'Last serviced e.g. 118,000 km' }, { t: 'T1', label: 'Reminder stages fire' }, { t: 'T2', label: 'Due at 128,000 km' }],
    example: 'Interval 10,000 km with Last Serviced 118,000 km → next due at 128,000 km.',
    pitfall: 'Reminder stages fire relative to this due point — set the interval before tuning the stages.',
  },
  interval_days: {
    title: 'Interval (days) — dual basis',
    body: ['The calendar leg of a dual schedule. Both countdowns run in parallel; the service falls due at whichever is reached first — the reading interval or this many days since the last service.'],
    example: '10,000 km + 180 days: a low-usage ute hits the 180-day mark first; a linehaul truck hits 10,000 km first.',
  },
  baseline_value: {
    title: 'Last Serviced (reading)',
    body: ['The odometer / engine-hours reading when this task was last done — the anchor the whole schedule counts from.', 'Next due = this reading + the interval. The vehicle\u2019s current live reading is shown under the field for reference.'],
    example: 'Last oil change at 118,000 km, interval 10,000 km → next due 128,000 km.',
    pitfall: 'If the past reading is unknown, enter the current reading — the first cycle then starts from today. When editing, a mismatch banner offers the value from the latest service log.',
  },
  baseline_date: {
    title: 'Last Serviced Date',
    body: ['When this task was last completed. Calendar schedules (and the calendar leg of dual schedules) count forward from this date.'],
    example: 'Last inspected 01/03/2026 with a 90-day interval → next due 30/05/2026.',
  },
  recurring: {
    title: 'Recurring',
    body: [
      'On — after each logged service the schedule rolls forward automatically to the next due point, indefinitely.',
      'Off — a one-off job: once serviced, the schedule completes and stops tracking.',
      'For recurring reading-based schedules, "Keep a fixed service schedule" controls HOW it rolls: fixed grid (10,000 / 20,000 / 30,000 …) vs floating (actual reading at service + interval).',
    ],
    timeline: [{ t: 'Due', label: 'Service logged' }, { t: '+i', label: 'Rolls forward by interval' }, { t: '…', label: 'Repeats each cycle' }],
    pitfall: 'Turn Recurring off for one-time jobs (a recall fix, a fitment) — otherwise it keeps rescheduling forever.',
  },
  stage_when: {
    title: 'Stage — When it fires',
    body: [
      'Each reminder stage fires once per service cycle, at its own point relative to the due point:',
      '• Before due — early warning at "X km / hrs / days before".',
      '• At due — exactly when the service falls due.',
      '• After due (overdue) — escalation once the service is late.',
    ],
    timeline: [{ t: '-1,000', label: 'Stage 1 · Before due' }, { t: 'Due', label: 'Stage 2 · At due' }, { t: '+500', label: 'Stage 3 · Overdue' }],
    example: 'Stage 1: 1,000 km before → email Parts. Stage 2: at due → Booking. Stage 3: 500 km overdue → Critical + manager SMS.',
    pitfall: 'All stages escalate ONE Service alert — each stage raises its severity and sends its own notifications; they never create separate alerts.',
  },
  stage_severity: {
    title: 'Stage Severity',
    body: ['The priority the Service alert takes when this stage fires — it drives the colour and ordering in the Alerts inbox and the badges on the Maintenance page.'],
    example: 'Low / Medium for early reminders, High at due, Critical once overdue.',
    pitfall: 'Severity is per stage, so the single alert escalates naturally as later stages fire.',
  },
  log_cost: {
    title: 'Cost',
    body: ['The total cost of this service — parts, labour and consumables. It feeds the Cost Report and the "Spend this month" tile on the Maintenance page.'],
    example: 'Oil + filter + labour = 289.50 → enter 289.50.',
    pitfall: 'Optional — leave blank if unknown and add it later via Edit Service Log; blank costs simply don\u2019t count toward the Cost Report.',
  },
  bulk_cost: {
    title: 'Cost (apply to all)',
    body: ['One cost copied onto every selected schedule\u2019s service log. Each vehicle row in the next step can still override it individually.'],
    example: '12 vehicles serviced at a flat 150 each → enter 150 once here instead of typing it 12 times.',
    pitfall: 'A per-vehicle Cost entered on a row below always wins over this shared value.',
  },
  bulk_row_cost: {
    title: 'Per-vehicle Cost',
    body: ['Overrides the shared "apply to all" cost for this vehicle only. Leave blank to use the shared cost.'],
    example: 'Flat 150 for the fleet, but this truck also needed brake pads → enter 320 on its row.',
  },
  log_reading: {
    title: 'Reading at service',
    body: [
      'The actual odometer (km) or engine-hours reading when the work was done — this becomes the new anchor the next due point counts from.',
      'Rolling schedules: next due = this reading + interval. Fixed schedules: the due grid stays anchored to the original plan; the reading is still recorded on the log.',
    ],
    timeline: [{ t: 'T0', label: 'Serviced at this reading' }, { t: '+i', label: 'Interval added' }, { t: 'Due', label: 'Next service due' }],
    example: 'Serviced at 128,340 km on a rolling 10,000 km schedule → next due 138,340 km.',
    pitfall: 'Required for reading-based schedules — a typo here shifts every future reminder, so match the workshop invoice figure.',
  },
  next_override: {
    title: 'Set next service at — optional override',
    body: [
      'Manually pins where the NEXT service falls due, replacing the computed value (reading + interval, or the fixed grid).',
      'Applies to this cycle only — once the next service is logged, the schedule returns to its normal interval logic.',
    ],
    example: 'The workshop stamps "next service 140,000 km" even though the interval math lands on 138,340 → enter 140000.',
    pitfall: 'Leave blank for normal behaviour. The override replaces the km / hrs due point only — the calendar leg of a dual schedule is unaffected.',
  },
  imp_vehicle: {
    title: 'Vehicle column',
    body: ['Matches the row to a fleet asset by name — asset, unit or device name, case-insensitive. Either this or the Device IMEI must identify the vehicle.'],
    example: '"Truck 12" matches the asset named Truck 12 on the Fleet page.',
    pitfall: 'Red "Vehicle not found" = no asset matches — copy the name exactly from Fleet, or fill Device IMEI instead.',
  },
  imp_imei: {
    title: 'Device IMEI column',
    body: ['An unambiguous way to identify the vehicle — the tracker\u2019s IMEI. Use it when vehicle names are missing, inconsistent or duplicated.'],
    example: 'Two assets both called "Truck 12" → the IMEI decides which one gets the schedule.',
  },
  imp_task: {
    title: 'Task Name column',
    body: ['Becomes the schedule\u2019s task. Vehicle + Task Name together identify an existing schedule — matching rows highlight amber as duplicates.'],
    example: 'Importing "Oil Change" for Truck 12 when that schedule already exists → the row turns amber.',
    pitfall: 'Amber rows can only be selected when duplicate handling is set to Update — otherwise they are skipped to protect existing schedules.',
  },
  imp_basis: {
    title: 'Basis column',
    body: [
      'What the schedule counts on. Accepted values (aliases in brackets):',
      '• odometer (km, mileage)',
      '• engine_hours (hours, hrs)',
      '• calendar (date, days)',
      '• odometer_calendar / engine_hours_calendar — dual: whichever limit is reached first.',
    ],
    pitfall: 'The Basis decides which review tab the row lands on and which columns are required.',
  },
  imp_interval_metric: {
    title: 'Interval (km / hrs) column',
    body: ['How much usage between services, in this tab\u2019s unit — km on the Odometer tab, hours on the Engine Hours tab. Required (> 0) for metric and dual rows.'],
    example: '10000 on an odometer row = service every 10,000 km.',
  },
  imp_last_metric: {
    title: 'Last Serviced (km / hrs) column',
    body: ['The reading when the task was last done — the anchor the first due point counts from: next due = this reading + interval.'],
    example: 'Last Serviced 118000, Interval 10000 → first due at 128,000 km.',
    pitfall: 'Required for metric and dual rows. If unknown, use the vehicle\u2019s current reading so the first cycle starts from today.',
  },
  imp_interval_days: {
    title: 'Interval (days) column',
    body: ['The calendar leg: days between services. Required (> 0) for calendar and dual rows; shows "—" (disabled) for pure odometer / engine-hours rows.'],
    example: '365 = annual rego renewal.',
  },
  imp_last_date: {
    title: 'Last Serviced Date column',
    body: ['When the task was last completed — the calendar countdown starts here. Must match the date format picker at the top of the wizard (DD/MM/YYYY, MM/DD/YYYY or YYYY-MM-DD).'],
    example: 'With DD/MM/YYYY selected, 01/03/2026 means 1 March 2026.',
    pitfall: 'Required for calendar and dual rows. A date in the wrong format flags an error — switch the format picker rather than retyping every date.',
  },
  imp_recurring: {
    title: 'Recurring column',
    body: [
      'yes — the schedule rolls forward automatically after each logged service. no — a one-off task that completes once serviced. Blank defaults to yes.',
      'The sheet\u2019s "Fixed Schedule" column (yes/no) additionally controls fixed-grid vs floating roll-forward for recurring metric rows.',
    ],
  },
  imp_status: {
    title: 'Status column',
    body: [
      'Row health at a glance:',
      '• Red — validation errors; fix the highlighted cells before the row can be selected.',
      '• Amber — matches an existing schedule (same vehicle + task); imported only in Update mode.',
      '• Ready — will be created as a new schedule.',
    ],
    pitfall: 'Reminder stages aren\u2019t part of the sheet — use "Clone from existing schedule" on selected rows to copy full reminder stages across.',
  },
  cost_trend: {
    title: 'Spend Trends',
    body: [
      'Monthly maintenance spend over the selected window, split by vehicle or by task type (use the Vehicles / Tasks toggle) — whichever bars or lines keep growing are your cost outliers.',
      'Click a bar segment or line point to filter the report below to that vehicle + month. Click a legend chip or a Top Spender to filter by vehicle only — the ✕ chip clears it.',
      'The ▲/▼ badge compares each vehicle\u2019s last 3-month average spend against the 3 months before it. Red ▲ = climbing fast (>15%). "new" = first costed spend with no prior baseline yet; "steady" = no change.',
    ],
    example: 'Truck 42 shows ▲ 35% while the rest of the fleet is steady → open its month to see which services drove the spike.',
    pitfall: 'Only services logged WITH a cost count here — a busy vehicle with blank costs will look artificially cheap.',
  },
};

export const MaintHelpTip = ({ topic, testId, className = '' }) => {
  const t = MAINT_HELP_TOPICS[topic];
  if (!t) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center shrink-0 text-muted-foreground hover:text-[#2563eb] transition-colors ${className}`}
          data-testid={testId || `maint-help-tip-${topic}`}
          aria-label={`Help: ${t.title}`}
        >
          <HelpCircle className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 max-w-[90vw] text-xs space-y-2 z-[10000]" side="right" align="start" data-testid={`maint-help-popover-${topic}`}>
        <p className="font-semibold text-sm">{t.title}</p>
        {t.body.map((b, i) => <p key={i} className="text-muted-foreground leading-relaxed">{b}</p>)}
        {t.timeline && <MiniTimeline steps={t.timeline} />}
        {t.example && (
          <div className="p-2 rounded bg-muted leading-relaxed"><span className="font-medium">Example: </span>{t.example}</div>
        )}
        {t.pitfall && (
          <div className="p-2 rounded bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 leading-relaxed"><span className="font-medium">Watch out: </span>{t.pitfall}</div>
        )}
      </PopoverContent>
    </Popover>
  );
};

// ─────────────────────────── 1 · Service Schedule ───────────────────────────

const SCHEDULE_BASIS = [
  ['Odometer (km)', 'Due at a distance — e.g. every 10,000 km. The engine compares each vehicle’s live odometer (GPS-accumulated or sensor) against the due point.'],
  ['Engine Hours', 'Due at a run-time — e.g. every 250 hours. Best for machinery, gensets and idling-heavy assets where distance is meaningless.'],
  ['Date', 'Due on a calendar date — e.g. every 6 months, or a one-off inspection on a fixed day.'],
  ['Dual basis', 'Odometer + Date or Engine Hours + Date together. Whichever comes first makes the service due (e.g. every 10,000 km OR 12 months).'],
];

const SCHEDULE_SCENARIOS = [
  {
    title: 'Oil change every 10,000 km with staged reminders',
    setup: 'Basis Odometer, interval 10,000 km, Recurring on. Stage 1: 1,000 km before → Medium, email Parts. Stage 2: 500 km before → High, email Booking. Stage 3: overdue → Critical.',
    flow: 'As the odometer climbs, each stage fires once and the single Service alert escalates to that stage’s severity. Logging the service resolves the alert and rolls the next due forward by 10,000 km.',
    note: 'Each stage carries its own severity, notifications and actions — reach different people at each point.',
  },
  {
    title: 'Annual safety inspection (one-off)',
    setup: 'Basis Date, Recurring off, due date set. Stage: 14 days before → email the manager.',
    flow: 'The reminder fires 14 days out; when the date passes the alert goes overdue. Logging it closes the alert and, because it is not recurring, the schedule completes.',
    note: 'Turn Recurring off for jobs that happen once; leave it on for anything that repeats.',
  },
  {
    title: 'Fixed vs floating next due',
    setup: 'Recurring service every 5,000 km. Fixed schedule ON vs OFF.',
    flow: 'Fixed anchors the next due to the original plan (5,000 · 10,000 · 15,000 …) even if you log late at 5,300 km. Floating adds the interval to the reading you logged (5,300 → 10,300).',
    note: 'Use Fixed for manufacturer intervals you must hold to; Floating when the clock should restart from the actual service.',
  },
];

const SCHEDULE_DIAGNOSTICS = [
  ['No reminder ever fires', 'Confirm the schedule is active and a reminder stage’s trigger point (its “When”) has actually been crossed. Use Run Check to evaluate immediately — the engine otherwise runs hourly.'],
  ['Due point never updates', 'Odometer/engine-hours schedules need live meter values. If the vehicle’s odometer source is Off there is nothing to compare against — set the source on the Fleet card, or use a Date basis instead.'],
  ['Reminder fired at the wrong distance', 'Each stage’s trigger point is measured relative to the due point (“X before”, “at due”, or “X overdue”). Check the stage’s When value and the schedule interval.'],
  ['Same alert keeps escalating', 'That is by design — one Service alert per schedule escalates through the stages as the vehicle gets closer. It resolves when you log the service.'],
];

export const ScheduleHelpPanel = () => (
  <Sheet>
    <SheetTrigger asChild>
      <Button type="button" variant="outline" size="sm" className="h-7 gap-1.5 text-xs" data-testid="service-schedule-help-btn">
        <BookOpen className="w-3.5 h-3.5" /> How the schedule works
      </Button>
    </SheetTrigger>
    <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto z-[10000]" data-testid="service-schedule-help-panel">
      <SheetHeader>
        <SheetTitle>How a Service Schedule Works</SheetTitle>
        <SheetDescription>From an interval and reminder stages to escalating service alerts.</SheetDescription>
      </SheetHeader>
      <HelpDocExport slug="service-schedule" title="How a Service Schedule Works" subtitle="From an interval and reminder stages to escalating service alerts" targetId="help-doc-service-schedule" />
      <div id="help-doc-service-schedule" className="space-y-6 py-4">
        <Section title="1 · What a schedule is">
          <p>A schedule tells the system <span className="font-medium text-foreground">when a vehicle is due</span> for a task (service, inspection, licence renewal…). You pick a <span className="font-medium text-foreground">basis</span>, an <span className="font-medium text-foreground">interval</span>, and add one or more <span className="font-medium text-foreground">reminder stages</span> that raise alerts and send notifications as the due point approaches.</p>
          <p><span className="font-medium text-foreground">Recurring</span> schedules roll forward automatically every time you log the service; non-recurring ones complete after one job.</p>
        </Section>

        <Section title="2 · The service lifecycle">
          <MiniTimeline steps={[
            { t: 'OK', label: 'Not near due — nothing to do' },
            { t: 'DUE SOON', label: 'A reminder stage’s trigger point is crossed → alert' },
            { t: 'OVERDUE', label: 'Past the due point → escalated alert' },
            { t: 'LOGGED', label: 'Service logged → alert resolved, next due rolls forward' },
          ]} />
          <p>The engine re-evaluates every schedule hourly (or immediately when you press <span className="font-medium text-foreground">Run Check</span>). Service alerts land in Analytics › Alerts alongside health alerts.</p>
        </Section>

        <Section title="3 · Choosing a basis">
          <div className="border rounded overflow-hidden">
            <table className="w-full text-[11px]">
              <thead><tr className="bg-muted text-foreground">
                <th className="text-left p-1.5 font-medium">Basis</th>
                <th className="text-left p-1.5 font-medium">Use it for</th>
              </tr></thead>
              <tbody className="divide-y">
                {SCHEDULE_BASIS.map(([b, w]) => (
                  <tr key={b}><td className="p-1.5 font-medium text-foreground align-top max-w-[120px] whitespace-normal">{b}</td><td className="p-1.5 align-top">{w}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="4 · Reminder stages">
          <p>Each stage has a <span className="font-medium text-foreground">When</span> (its trigger point — e.g. 1,000 km before due, at due, or 3 days overdue), a <span className="font-medium text-foreground">Severity</span>, and its own <span className="font-medium text-foreground">Notifications</span> (in-app, email, SMS, push) and <span className="font-medium text-foreground">Actions</span> (webhook / device command).</p>
          <p>Stages let you build an escalation: a gentle heads-up early, a firmer reminder close to due, and a critical alert once overdue — each to whoever needs it.</p>
        </Section>

        <Section title="5 · Fixed vs floating next due">
          <p><span className="font-medium text-foreground">Fixed schedule ON</span> keeps the next due locked to the original plan even if you log late, so intervals never drift.</p>
          <p><span className="font-medium text-foreground">Fixed OFF (floating)</span> restarts the clock from the reading/date you actually logged.</p>
        </Section>

        <Section title="6 · Common scenarios">
          {SCHEDULE_SCENARIOS.map((s, i) => (
            <div key={i} className="p-2.5 rounded border bg-muted/40 space-y-1">
              <p className="font-medium text-foreground">{s.title}</p>
              <p><span className="font-medium">Setup:</span> {s.setup}</p>
              <p><span className="font-medium">Flow:</span> {s.flow}</p>
              <p className="text-amber-700 dark:text-amber-400">{s.note}</p>
            </div>
          ))}
        </Section>

        <Section title="7 · Why isn't it working?">
          {SCHEDULE_DIAGNOSTICS.map(([q, a], i) => (
            <div key={i}>
              <p className="font-medium text-foreground">{q}</p>
              <p>{a}</p>
            </div>
          ))}
        </Section>
      </div>
    </SheetContent>
  </Sheet>
);

// ─────────────────────────── 2 · Log Service ───────────────────────────

const LOG_DIAGNOSTICS = [
  ['“Duplicate log” warning appears', 'A service for this vehicle + task was already logged in the last 24 hours. Confirm to log it anyway, or cancel if it was a mistake.'],
  ['Next due looks wrong after logging', 'That depends on Fixed vs floating on the schedule. Fixed holds the original plan; floating adds the interval to the reading you entered. You can also type a manual next-due override before saving.'],
  ['Meter reading is required', 'Odometer / engine-hours schedules need the reading at the time of service so the next due can be calculated. Date-only schedules don’t ask for a meter value.'],
  ['Can’t attach a document', 'Attachments upload one at a time; wait for each to finish. Very large files may exceed the upload limit — keep receipts/photos reasonably sized.'],
];

export const LogServiceHelpPanel = () => (
  <Sheet>
    <SheetTrigger asChild>
      <Button type="button" variant="outline" size="sm" className="h-7 gap-1.5 text-xs" data-testid="log-service-help-btn">
        <BookOpen className="w-3.5 h-3.5" /> How logging works
      </Button>
    </SheetTrigger>
    <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto z-[10000]" data-testid="log-service-help-panel">
      <SheetHeader>
        <SheetTitle>How Logging a Service Works</SheetTitle>
        <SheetDescription>Record a completed job, resolve its alert and roll the schedule forward.</SheetDescription>
      </SheetHeader>
      <HelpDocExport slug="log-service" title="How Logging a Service Works" subtitle="Record a completed job, resolve its alert and roll the schedule forward" targetId="help-doc-log-service" />
      <div id="help-doc-log-service" className="space-y-6 py-4">
        <Section title="1 · What logging does">
          <p>Logging records a <span className="font-medium text-foreground">completed service</span> against the schedule. It writes a permanent entry in <span className="font-medium text-foreground">Service History</span>, resolves any open Service alert, and — for recurring schedules — <span className="font-medium text-foreground">rolls the next due forward</span>.</p>
        </Section>

        <Section title="2 · What to fill in">
          <div className="border rounded overflow-hidden">
            <table className="w-full text-[11px]">
              <thead><tr className="bg-muted text-foreground">
                <th className="text-left p-1.5 font-medium">Field</th>
                <th className="text-left p-1.5 font-medium">Why it matters</th>
              </tr></thead>
              <tbody className="divide-y">
                <tr><td className="p-1.5 font-medium text-foreground align-top">Completed date</td><td className="p-1.5 align-top">When the work was actually done — drives date-based next due.</td></tr>
                <tr><td className="p-1.5 font-medium text-foreground align-top">Meter reading</td><td className="p-1.5 align-top">Odometer / engine hours at service — used to calculate the next due for those bases.</td></tr>
                <tr><td className="p-1.5 font-medium text-foreground align-top">Cost</td><td className="p-1.5 align-top">Feeds the Cost Report and spend totals.</td></tr>
                <tr><td className="p-1.5 font-medium text-foreground align-top">Vendor / Performed by</td><td className="p-1.5 align-top">Who did the work — searchable in Service History.</td></tr>
                <tr><td className="p-1.5 font-medium text-foreground align-top">Notes & attachments</td><td className="p-1.5 align-top">Invoices, photos and remarks kept with the record.</td></tr>
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="3 · What happens next">
          <MiniTimeline steps={[
            { t: 'SAVE', label: 'History entry written & cost recorded' },
            { t: 'RESOLVE', label: 'Open Service alert for this schedule closes' },
            { t: 'ROLL', label: 'Recurring? Next due recalculated (Fixed / floating)' },
          ]} />
          <p>A <span className="font-medium text-foreground">next-due override</span> lets you set the next due manually — handy after an out-of-cycle job. Non-recurring schedules simply complete.</p>
        </Section>

        <Section title="4 · Bulk logging">
          <p>Use <span className="font-medium text-foreground">Bulk Log</span> on the Schedules toolbar to log the same job across many vehicles at once — pick the task and vehicles, then enter each vehicle’s reading and cost.</p>
        </Section>

        <Section title="5 · Troubleshooting">
          {LOG_DIAGNOSTICS.map(([q, a], i) => (
            <div key={i}>
              <p className="font-medium text-foreground">{q}</p>
              <p>{a}</p>
            </div>
          ))}
        </Section>
      </div>
    </SheetContent>
  </Sheet>
);

// ─────────────────────────── 3 · Edit Service Log ───────────────────────────

export const EditServiceLogHelpPanel = () => (
  <Sheet>
    <SheetTrigger asChild>
      <Button type="button" variant="outline" size="sm" className="h-7 gap-1.5 text-xs" data-testid="edit-service-log-help-btn">
        <BookOpen className="w-3.5 h-3.5" /> How editing works
      </Button>
    </SheetTrigger>
    <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto z-[10000]" data-testid="edit-service-log-help-panel">
      <SheetHeader>
        <SheetTitle>How Editing a Service Log Works</SheetTitle>
        <SheetDescription>Correct a past record without disturbing the schedule’s next due.</SheetDescription>
      </SheetHeader>
      <HelpDocExport slug="edit-service-log" title="How Editing a Service Log Works" subtitle="Correct a past record without disturbing the schedule's next due" targetId="help-doc-edit-service-log" />
      <div id="help-doc-edit-service-log" className="space-y-6 py-4">
        <Section title="1 · What editing changes">
          <p>Editing updates an <span className="font-medium text-foreground">existing Service History record</span> — its date, meter reading, cost, vendor, who performed it, notes and attachments. This is for fixing mistakes or filling in details on a job you already logged.</p>
        </Section>

        <Section title="2 · What it does NOT change">
          <p>Editing a history entry <span className="font-medium text-foreground">does not re-roll the schedule’s next due</span>. The next due was set when the service was first logged (or via an override). Changing an old record’s figures corrects your history and cost reporting, but the live due point stays put.</p>
          <p>If a schedule’s next due is genuinely wrong, fix it on the schedule itself (edit the schedule or log the next service with an override) rather than here.</p>
        </Section>

        <Section title="3 · Audit trail">
          <p>Every edit is stamped — the record shows who originally logged it and who last edited it, with timestamps in your local timezone. Cost and reading changes flow straight through to the Cost Report.</p>
        </Section>

        <Section title="4 · Deleting instead">
          <p>To remove a log entirely (e.g. a duplicate), delete it from the Service History row rather than editing. Deleting a record removes its cost from reports but, like editing, leaves the schedule’s current due point unchanged.</p>
        </Section>
      </div>
    </SheetContent>
  </Sheet>
);

// ─────────────────────────── 4 · Import Wizard ───────────────────────────

const IMPORT_STEPS = [
  ['Asset Name', 'The vehicle/asset name as shown in Fleet — the primary way rows are matched to a vehicle.'],
  ['Unit Number', 'Falls back to the unit number if the asset name doesn’t match.'],
  ['Device Name', 'Falls back to the tracker/device name.'],
  ['IMEI (optional)', 'A tie-breaker when names are ambiguous. Leave blank unless you need it — spreadsheets can mangle long IMEI numbers.'],
];

const IMPORT_DIAGNOSTICS = [
  ['“Vehicle not found” on a row', 'The Vehicle value didn’t match any Asset name, Unit number or Device name (and IMEI, if given). Check spelling/spacing against the Fleet list, or add an IMEI to disambiguate.'],
  ['Dates import wrong', 'Templates and exports use your Settings date format, and the importer auto-corrects common alternatives. If a date still looks off, match the format shown in the column placeholder.'],
  ['Reminder stages aren’t in the template', 'By design — a reminder needs a “When” to be meaningful, which the flat spreadsheet can’t express safely. Add stages with the Clone feature instead (copy from an existing schedule of the same basis).'],
  ['I imported the wrong file', 'Use Undo within 30 minutes of the import. It restores any schedules the run updated and deletes the ones it created.'],
];

export const ImportHelpPanel = () => (
  <Sheet>
    <SheetTrigger asChild>
      <Button type="button" variant="outline" size="sm" className="h-7 gap-1.5 text-xs" data-testid="schedule-import-help-btn">
        <BookOpen className="w-3.5 h-3.5" /> How import works
      </Button>
    </SheetTrigger>
    <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto z-[10000]" data-testid="schedule-import-help-panel">
      <SheetHeader>
        <SheetTitle>How the Import Wizard Works</SheetTitle>
        <SheetDescription>Bulk-create or update schedules from a spreadsheet — safely and reversibly.</SheetDescription>
      </SheetHeader>
      <HelpDocExport slug="schedule-import" title="How the Import Wizard Works" subtitle="Bulk-create or update schedules from a spreadsheet — safely and reversibly" targetId="help-doc-schedule-import" />
      <div id="help-doc-schedule-import" className="space-y-6 py-4">
        <Section title="1 · The four steps">
          <MiniTimeline steps={[
            { t: 'UPLOAD', label: 'Drop an .xlsx / .csv (start from the template)' },
            { t: 'REVIEW', label: 'Fix any flagged rows inline' },
            { t: 'DRY-RUN', label: 'Preview what will be created / updated' },
            { t: 'RESULT', label: 'Import — undoable for 30 minutes' },
          ]} />
          <p>Always start from the <span className="font-medium text-foreground">Download template</span> — it carries the right columns and worked examples for each basis (odometer, engine hours, date, and dual basis).</p>
        </Section>

        <Section title="2 · Basis tabs">
          <p>The wizard groups rows by basis — <span className="font-medium text-foreground">Odometer</span>, <span className="font-medium text-foreground">Engine Hours</span> and <span className="font-medium text-foreground">Date</span>. A dual-basis schedule (e.g. km + date) stays in its metric tab with the date fields alongside. This keeps each tab’s fields relevant and prevents mixing incompatible values.</p>
        </Section>

        <Section title="3 · Matching a row to a vehicle">
          <div className="border rounded overflow-hidden">
            <table className="w-full text-[11px]">
              <thead><tr className="bg-muted text-foreground">
                <th className="text-left p-1.5 font-medium">Column</th>
                <th className="text-left p-1.5 font-medium">How it’s used</th>
              </tr></thead>
              <tbody className="divide-y">
                {IMPORT_STEPS.map(([c, w]) => (
                  <tr key={c}><td className="p-1.5 font-medium text-foreground align-top max-w-[130px] whitespace-normal">{c}</td><td className="p-1.5 align-top">{w}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="4 · Create vs Update">
          <p><span className="font-medium text-foreground">Create-only</span> (default) adds new schedules and skips any that already exist. <span className="font-medium text-foreground">Update-existing</span> matches by vehicle + task and edits the matched schedule, so you can bulk-adjust intervals.</p>
          <p>The dry-run tells you exactly how many rows will be created, updated, skipped, or have errors before anything is written.</p>
        </Section>

        <Section title="5 · Reminder stages via Clone">
          <p>Reminder stages aren’t part of the spreadsheet. To add rich, multi-stage reminders, use <span className="font-medium text-foreground">Clone</span> — copy the stage configuration from an existing schedule of the <span className="font-medium">same basis</span> onto the selected imported schedules (the same pattern as the Device import wizard’s clone).</p>
        </Section>

        <Section title="6 · Undo & troubleshooting">
          {IMPORT_DIAGNOSTICS.map(([q, a], i) => (
            <div key={i}>
              <p className="font-medium text-foreground">{q}</p>
              <p>{a}</p>
            </div>
          ))}
        </Section>
      </div>
    </SheetContent>
  </Sheet>
);
